import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";

import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import DataTable from "../components/common/DataTable";
import SearchBar from "../components/common/SearchBar";
import FilterDropdown from "../components/common/FilterDropdown";
import MobileFilterPanel from "../components/common/MobileFilterPanel";
import StatusToggle from "../components/common/StatusToggle";
import ExportExcelModal from "../components/modals/ExportExcelModal";
import { EditButton, DeleteButton } from "../components/common/ActionButtons";

import { showError, showSuccess } from "../utils/toast";
import { confirmDelete, showConfirm } from "../utils/confirm";
import { formatDateShort } from "../utils/dateFormat";
import { getProjectName } from "../utils/projectHelpers";

import { JWT, ROUTES, PAGINATION } from "../config/constants";
import { useCurrentUser } from "../hooks/useCurrentUser";

import apiClient, { API_BASE_URL } from "../api/apiClient";

import {
  getVaVes,
  getVaVeCategories,
  deleteVaVe,
} from "../services/vaveService";

import ExportExcelButton from "../components/common/ExportExcelButton";

export default function VaVeList() {
  const navigate = useNavigate();

  const { role } = useCurrentUser();

  const isProjectAdmin = role?.toLowerCase() === "project_admin";
  const isSuperAdmin = role?.toLowerCase() === "super_admin";
  const isAdmin = isProjectAdmin || isSuperAdmin;
  const canApprove = isProjectAdmin || isSuperAdmin;

  const [vaves, setVaves] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filter State
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  // Search State
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  // Mobile temp filter
  const [tempProject, setTempProject] = useState("");
  const [tempCategory, setTempCategory] = useState("");
  const [tempStatus, setTempStatus] = useState("");
  const [tempSort, setTempSort] = useState("newest");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);

  const [showExportModal, setShowExportModal] = useState(false);

  const limit = PAGINATION.DEFAULT_PAGE_SIZE;

  const columns = [
    {
      field: "area_item",
      label: "Area / Item",
      className: "min-w-[260px]",
    },
    {
      field: "existing_design",
      label: "Existing Design",
      className: "min-w-[220px]",
    },
    {
      field: "usulan_vave",
      label: "Usulan VA/VE",
      className: "min-w-[220px]",
    },
    {
      field: "benefit",
      label: "Benefit",
      className: "min-w-[220px]",
    },
    {
      field: "risk_mitigation",
      label: "Risk & Mitigasi",
      className: "min-w-[220px]",
    },
    {
      field: "project",
      label: "Project",
      className: "w-[180px]",
    },
    {
      field: "status",
      label: "Status",
      className: "w-[170px] text-center",
    },
  ];

  // ANIMASI LOADING
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  // INIT
  useEffect(() => {
    loadProjects();
    loadCategories();

    const resize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", resize);

    return () => window.removeEventListener("resize", resize);
  }, []);

  // SEARCH DEBOUNCE
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchKeyword(searchInput);
      setPage(1); // Reset to first page when search changes
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  // FETCH
  useEffect(() => {
    fetchData();
  }, [
    page,
    selectedProject,
    selectedCategory,
    selectedStatus,
    sortBy,
    searchKeyword,
  ]);

  async function loadProjects() {
    try {
      const res = await apiClient.get("/project-names");
      setProjects(res.data || []);
    } catch {
      showError("Gagal memuat project");
    }
  }

  async function loadCategories() {
    try {
      const data = await getVaVeCategories();
      setCategories(data);
    } catch {
      showError("Gagal memuat kategori");
    }
  }

async function fetchData() {
    setLoading(true);

    try {
      const params = {
        page,
        limit,
        sort_by: sortBy,
      };

      if (selectedProject) params.project_id = selectedProject;
      if (selectedCategory) params.category_id = selectedCategory;
      if (selectedStatus) params.status = selectedStatus;
      if (searchKeyword) params.search = searchKeyword;

      const data = await getVaVes(params);

      if (Array.isArray(data)) {
        // --- LOGIKA PAGINASI FRONTEND ---
        // Jika backend belum support paginasi dan mengembalikan semua data sekaligus
        
        // 1. Potong array data sesuai dengan halaman (page) yang sedang aktif
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = data.slice(startIndex, endIndex);

        // 2. Set state dengan data yang sudah dipotong
        setVaves(paginatedData); 
        
        // 3. Hitung total halaman (Total Data / Limit per halaman)
        setTotalPages(Math.ceil(data.length / limit)); 
        setTotalData(data.length);
      } else {
        // Jika backend sudah support paginasi (mengembalikan object seperti Lesson)
        setVaves(data.data || []);
        setTotalPages(data.total_pages || 1);
        setTotalData(data.total || 0);
      }
    } catch {
      showError("Gagal memuat data VA/VE");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id, title) {
    confirmDelete(`VA/VE "${title}"`, async () => {
      try {
        await deleteVaVe(id);
        showSuccess("Data berhasil dihapus");
        fetchData();
      } catch {
        showError("Gagal menghapus data");
      }
    });
  }

  function handleApprove(id) {
    showConfirm({
      title: "Setujui VA/VE?",
      message: "Data akan diapprove.",
      confirmLabel: "Approve",
      cancelLabel: "Batal",
      confirmColor: "blue",
      onConfirm: async () => {
        try {
          await apiClient.put(`/vave/${id}/approve`);
          showSuccess("Berhasil approve");
          fetchData();
        } catch {
          showError("Gagal approve");
        }
      },
    });
  }

  function handleUnapprove(id, title) {
    showConfirm({
      title: "Batalkan Approve?",
      message: `${title} akan kembali pending.`,
      confirmLabel: "Ya",
      cancelLabel: "Batal",
      confirmColor: "red",
      onConfirm: async () => {
        try {
          await apiClient.put(`/vave/${id}/unapprove`);
          showSuccess("Berhasil unapprove");
          fetchData();
        } catch {
          showError("Gagal unapprove");
        }
      },
    });
  }

  // FILTER HANDLERS
  function handleProjectChange(value) {
    if (isMobile) {
      setTempProject(value);
    } else {
      setSelectedProject(value);
      setPage(1);
    }
  }

  function handleCategoryChange(value) {
    if (isMobile) {
      setTempCategory(value);
    } else {
      setSelectedCategory(value);
      setPage(1);
    }
  }

  function handleStatusChange(value) {
    if (isMobile) {
      setTempStatus(value);
    } else {
      setSelectedStatus(value);
      setPage(1);
    }
  }

  function handleSortChange(value) {
    if (isMobile) {
      setTempSort(value);
    } else {
      setSortBy(value);
      setPage(1);
    }
  }

  function handleApplyFilters() {
    setSelectedProject(tempProject);
    setSelectedCategory(tempCategory);
    setSelectedStatus(tempStatus);
    setSortBy(tempSort);
    setPage(1);
  }

  function handleResetFilters() {
    setTempProject("");
    setTempCategory("");
    setTempStatus("");
    setTempSort("newest");
  }

  // Animasi Loading State
  if (pageLoading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white overflow-hidden flex items-center justify-center">
        {/* Background Glow */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-cyan-200/40 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-blue-200/40 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "6s" }}
          />
          <div
            className="absolute top-[40%] left-[45%] w-[260px] h-[260px] bg-sky-200/30 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: "8s" }}
          />
          {/* Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
        </div>

        {/* Loader */}
        <div className="relative z-10 flex flex-col items-center">
          <div className="w-24 h-24 rounded-3xl bg-white/80 backdrop-blur-xl border border-gray-200 shadow-2xl flex items-center justify-center">
            <Loader2 size={42} className="text-cyan-600 animate-spin" />
          </div>
          <p className="mt-6 text-sm text-gray-600 tracking-wide">
            Loading Platform...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pt-20">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-5">
        <PageTitle>VA / VE</PageTitle>

        <div className="relative z-30 flex gap-2">
          <ExportExcelButton onClick={() => setShowExportModal(true)} />
          <AddButton
            onClick={() => navigate(ROUTES.VAVE_CREATE)}
            label="Tambah VA/VE"
          />
        </div>
      </div>

      {/* MOBILE FILTER */}
      <MobileFilterPanel
        searchValue={searchInput}
        onSearchChange={(e) => setSearchInput(e.target.value)}
        filters={[
          {
            label: "Project",
            options: [
              { value: "", label: "Semua Project" },
              ...projects.map((proj) => ({
                value: proj.id,
                label: proj.division
                  ? `${proj.name} (${proj.division})`
                  : proj.name,
              })),
            ],
            value: tempProject,
            onChange: handleProjectChange,
            show: isAdmin,
          },
          {
            label: "Kategori",
            options: [
              { value: "", label: "Semua Kategori" },
              ...categories.map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ],
            value: tempCategory,
            onChange: handleCategoryChange,
          },
          {
            label: "Status",
            options: [
              { value: "", label: "Semua Status" },
              { value: "Pending", label: "Pending" },
              { value: "Approved", label: "Approved" },
            ],
            value: tempStatus,
            onChange: handleStatusChange,
          },
          {
            label: "Urutkan",
            options: [
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ],
            value: tempSort,
            onChange: handleSortChange,
          },
        ]}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* DESKTOP FILTER */}
      <div className="hidden md:flex gap-4 mb-4">
        <div className="flex-1">
          <SearchBar
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cari VA/VE..."
          />
        </div>

        {isAdmin && (
          <div className="w-64">
            <FilterDropdown
              label="Project"
              value={selectedProject}
              onChange={handleProjectChange}
              options={[
                { value: "", label: "Semua Project" },
                ...projects.map((proj) => ({
                  value: proj.id,
                  label: proj.division ? (
                    <span>
                      {proj.name}{" "}
                      <span className="text-xs text-gray-400">
                        ({proj.division})
                      </span>
                    </span>
                  ) : (
                    proj.name
                  ),
                })),
              ]}
            />
          </div>
        )}

        <div className="w-48">
          <FilterDropdown
            label="Kategori"
            value={selectedCategory}
            onChange={handleCategoryChange}
            options={[
              { value: "", label: "Semua Kategori" },
              ...categories.map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]}
          />
        </div>

        <div className="w-48">
          <FilterDropdown
            label="Status"
            value={selectedStatus}
            onChange={handleStatusChange}
            options={[
              { value: "", label: "Semua Status" },
              { value: "Pending", label: "Pending" },
              { value: "Approved", label: "Approved" },
            ]}
          />
        </div>

        <div className="w-44">
          <FilterDropdown
            label="Urutkan"
            value={sortBy}
            onChange={handleSortChange}
            options={[
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ]}
          />
        </div>
      </div>

      {/* INFO */}
      <div className="mb-4 text-gray-600">
        Menampilkan {vaves.length} dari {totalData} data
      </div>

      {/* TABLE */}
      <DataTable
        columns={columns}
        data={vaves}
        loading={loading}
        emptyMessage="Belum ada data VA/VE"
        pagination={{
          page,
          totalPages,
          onPageChange: setPage,
        }}
        renderRow={(item) => (
          <tr
            key={item.id}
            className="hover:bg-gray-50 hover:cursor-pointer"
            onClick={() => navigate(ROUTES.VAVE_DETAIL(item.id))}
          >
            {/* AREA ITEM */}
            <td className="px-6 py-4">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-900 leading-relaxed">
                  {item.area_item}
                </span>

                {/* Tanggal */}
                <span className="text-xs text-gray-400">
                  {formatDateShort(item.created_at)}
                </span>

                {/* Kategori */}
                {item.category_name && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full w-fit bg-cyan-600 text-white">
                    {item.category_name}
                  </span>
                )}
              </div>
            </td>

            {/* EXISTING */}
            <td className="px-6 py-4">
              <span className="text-xs text-gray-600">
                {item.existing_design}
              </span>
            </td>

            {/* USULAN */}
            <td className="px-6 py-4">
              <span className="text-xs text-gray-600">
                {item.usulan_vave}
              </span>
            </td>

            {/* BENEFIT */}
            <td className="px-6 py-4">
              <span className="text-xs text-gray-600">
                {item.benefit}
              </span>
            </td>

            {/* RISK */}
            <td className="px-6 py-4">
              <span className="text-xs text-gray-600">
                {item.risk_mitigation}
              </span>
            </td>

            {/* PROJECT */}
            <td className="px-6 py-4">
              <div>
                <span className="text-xs text-gray-600">
                  {item.project_name ||
                    getProjectName(projects, item.project_id) ||
                    "-"}
                </span>

                {item.project_division && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {item.project_division}
                  </p>
                )}
              </div>
            </td>

            {/* STATUS + ACTION */}
            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col items-center gap-3">
                {/* STATUS */}
                <StatusToggle
                  status={item.status}
                  canEdit={canApprove}
                  onApprove={() => handleApprove(item.id)}
                  onUnapprove={() => handleUnapprove(item.id, item.area_item)}
                />

                {/* ACTION */}
                <div className="flex items-center justify-center gap-2">
                  <EditButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(ROUTES.VAVE_EDIT(item.id));
                    }}
                    size={16}
                  />

                  <DeleteButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id, item.area_item);
                    }}
                    size={16}
                  />
                </div>
              </div>
            </td>
          </tr>
        )}
      />

      {/* EXPORT */}
      {isAdmin && (
        <ExportExcelModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          projects={projects}
        />
      )}
    </div>
  );
}
